/**
 * Centralized logging + telemetry for authentication failures.
 *
 * Everything auth-related funnels through here so failures are never silently
 * swallowed. Today the sink is the browser console (structured, prefixed), but a
 * real telemetry backend (App Insights, Sentry, etc.) can be wired in by setting
 * `window.__authTelemetrySink` or by extending `emit()` below — without touching
 * any of the call sites.
 */

export type AuthEvent =
    | 'silent_token_failed'
    | 'interaction_required'
    | 'no_active_account'
    | 'api_unauthorized'
    | 'api_forbidden'
    | 'reauth_started'
    | 'reauth_succeeded'
    | 'reauth_failed'
    | 'login_failed'
    | 'login_cancelled'
    | 'logout'
    | 'msal_acquire_token_failure'
    | 'msal_login_failure'
    | 'msal_sso_silent_failure';

export interface AuthEventDetail {
    message?: string;
    errorCode?: string;
    status?: number;
    url?: string;
    correlationId?: string;
    [key: string]: unknown;
}

interface AuthTelemetryRecord extends AuthEventDetail {
    event: AuthEvent;
    level: 'info' | 'warn' | 'error';
    timestamp: string;
}

type TelemetrySink = (record: AuthTelemetryRecord) => void;

declare global {
    interface Window {
        /** Optional external telemetry sink (App Insights / Sentry adapter). */
        __authTelemetrySink?: TelemetrySink;
        /** Last N auth events, exposed for in-browser diagnostics. */
        __authEventLog?: AuthTelemetryRecord[];
    }
}

const MAX_BUFFERED_EVENTS = 50;

const normaliseError = (error: unknown): Pick<AuthEventDetail, 'message' | 'errorCode'> => {
    if (!error) {
        return {};
    }

    if (typeof error === 'string') {
        return { message: error };
    }

    const err = error as { message?: string; errorCode?: string; name?: string };
    return {
        message: err.message ?? err.name,
        errorCode: err.errorCode
    };
};

const emit = (level: AuthTelemetryRecord['level'], event: AuthEvent, detail: AuthEventDetail) => {
    const record: AuthTelemetryRecord = {
        event,
        level,
        timestamp: new Date().toISOString(),
        ...detail
    };

    // Structured console output so failures are always visible during dev/support.
    const consoleFn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.info;
    consoleFn(`[auth:${event}]`, record);

    if (typeof window !== 'undefined') {
        const buffer = (window.__authEventLog ??= []);
        buffer.push(record);
        if (buffer.length > MAX_BUFFERED_EVENTS) {
            buffer.splice(0, buffer.length - MAX_BUFFERED_EVENTS);
        }

        try {
            window.__authTelemetrySink?.(record);
        } catch {
            // Never let a telemetry sink failure break the auth flow.
        }
    }
};

export const logAuthFailure = (
    event: AuthEvent,
    detail: AuthEventDetail = {},
    error?: unknown
): void => {
    emit('error', event, { ...normaliseError(error), ...detail });
};

export const logAuthWarning = (
    event: AuthEvent,
    detail: AuthEventDetail = {},
    error?: unknown
): void => {
    emit('warn', event, { ...normaliseError(error), ...detail });
};

export const logAuthInfo = (event: AuthEvent, detail: AuthEventDetail = {}): void => {
    emit('info', event, detail);
};
